import type { FieldDefinition } from '../data/model';
import { attributeRuleField, attributeRuleFields } from './fields';
import type {
  AttributeRule,
  AttributeRuleCondition,
  AttributeRuleGroup,
  AttributeRuleOperator,
} from './model';
import { attributeRuleOperatorDefinition } from './operators';

export type AttributeRulePath = readonly number[];

export function createDefaultAttributeRuleCondition(
  fields: readonly FieldDefinition[],
): AttributeRuleCondition | null {
  const field = attributeRuleFields(fields)[0];
  return field
    ? { kind: 'condition', field: field.name, operator: 'is-not-empty', value: null }
    : null;
}

export function createDefaultAttributeRuleGroup(
  fields: readonly FieldDefinition[],
): AttributeRuleGroup | null {
  const condition = createDefaultAttributeRuleCondition(fields);
  return condition ? { kind: 'group', operator: 'and', children: [condition] } : null;
}

export function attributeRuleConditionWithField(
  condition: AttributeRuleCondition,
  fieldName: string,
  fields: readonly FieldDefinition[],
): AttributeRuleCondition | null {
  if (condition.field === fieldName) return condition;
  const field = attributeRuleField(fields, fieldName);
  return field
    ? { kind: 'condition', field: field.name, operator: 'is-not-empty', value: null }
    : null;
}

export function attributeRuleConditionWithOperator(
  condition: AttributeRuleCondition,
  operator: AttributeRuleOperator,
  fields: readonly FieldDefinition[],
): AttributeRuleCondition | null {
  const field = attributeRuleField(fields, condition.field);
  const definition = field ? attributeRuleOperatorDefinition(field, operator) : null;
  if (!definition) return null;
  const value =
    definition.valueKind === 'none'
      ? null
      : definition.valueKind === 'number'
        ? typeof condition.value === 'number' && Number.isFinite(condition.value)
          ? condition.value
          : 0
        : typeof condition.value === 'string'
          ? condition.value
          : '';
  return { ...condition, operator, value };
}

function transformAtPath(
  rule: AttributeRule,
  path: AttributeRulePath,
  transform: (current: AttributeRule) => AttributeRule | null,
): AttributeRule | null {
  if (path.length === 0) return transform(rule);
  if (rule.kind !== 'group') return rule;
  const [index, ...remainder] = path;
  if (index === undefined || index < 0 || index >= rule.children.length) return rule;
  const child = rule.children[index];
  if (!child) return rule;
  const transformed = transformAtPath(child, remainder, transform);
  const children = transformed
    ? rule.children.map((candidate, childIndex) => (childIndex === index ? transformed : candidate))
    : rule.children.filter((_, childIndex) => childIndex !== index);
  return children.length === 0 ? null : { ...rule, children };
}

export function updateAttributeRuleAtPath(
  root: AttributeRuleGroup,
  path: AttributeRulePath,
  transform: (current: AttributeRule) => AttributeRule | null,
): AttributeRuleGroup | null {
  const result = transformAtPath(root, path, transform);
  return result?.kind === 'group' ? result : null;
}

export function addAttributeRuleChild(
  root: AttributeRuleGroup,
  groupPath: AttributeRulePath,
  child: AttributeRule,
): AttributeRuleGroup {
  return (
    updateAttributeRuleAtPath(root, groupPath, (current) =>
      current.kind === 'group' ? { ...current, children: [...current.children, child] } : current,
    ) ?? root
  );
}

export function removeAttributeRuleAtPath(
  root: AttributeRuleGroup,
  path: AttributeRulePath,
): AttributeRuleGroup | null {
  return updateAttributeRuleAtPath(root, path, () => null);
}

export function countAttributeRuleConditions(rule: AttributeRule): number {
  return rule.kind === 'condition'
    ? 1
    : rule.children.reduce((total, child) => total + countAttributeRuleConditions(child), 0);
}
